import formatMoney from "lib/formatMoney";
import dayjs from "dayjs";
import Box from "@mui/material/Box";

// Helper function to validate LTV against available tiers
const validateLTVAgainstTiers = (value, additionalData) => {
  const loanQuoteData = additionalData?.LoanQuoteData;

  // Find the highest numeric LTV key in loanQuoteData and ensure value does not exceed it
  if (loanQuoteData && typeof loanQuoteData === "object") {
    // Get all keys that are numbers (LTVs)
    const ltvKeys = Object.keys(loanQuoteData)
      .map((key) => parseFloat(key))
      .filter((num) => !isNaN(num));
    if (ltvKeys.length > 0) {
      const maxLTV = Math.max(...ltvKeys);

      if (parseFloat(value) > maxLTV) {
        return { isValid: false, maxLTV };
      }
    }
  }

  return { isValid: true };
};

const DSCRCalculatorQuestions = [
  {
    q_id: "0",
    q_title: "Loan amortization",
    q_description: (answers, additionalData) => {
      return "Choose between fully amortizing (principal + interest) or interest-only payments. Interest-only loans typically have lower monthly payments but don't build equity.";
    },
    node_type: "node",
    a_type: "mc",
    a_input_type: "",
    a_opts: (answers) => {
      return ["Fully amortizing", "Interest only"];
    },
    q_next_step_func: (answer, answers, questions) => {
      return answer.q_id + "0";
    },
    q_child_nodes: [0],
    q_validation_func: (value, answer, additionalData) => {
      return true; //isValid
    },
    q_invalid_answer_help_text: (value, answer, additionalData) => "",
    a_error: false,
  },
  {
    q_id: "00",
    q_title: "Monthly rent",
    q_description: (answers, additionalData) => {
      return "What do you think you can rent the property for?";
    },
    node_type: "node",
    a_type: "currency",
    a_input_type: "number",
    a_opts: (answers) => {
      return [];
    },
    q_next_step_func: (answer, answers, questions) => {
      return answer.q_id + "0";
    },
    q_child_nodes: [0],
    q_validation_func: (value, answer, additionalData) => {
      if (value < 0) {
        return false; //isValid
      }
      return true; //isValid
    },
    q_invalid_answer_help_text: (value, answer, additionalData) =>
      "Please enter a value above 0",
    a_error: false,
  },
  {
    q_id: "000",
    q_title: "Annual taxes",
    q_description: (answers, additionalData) => {
      return "What is actual or estimated annual tax?";
    },
    node_type: "node",
    a_type: "currency",
    a_input_type: "number",
    a_opts: (answers) => {
      return [];
    },
    q_next_step_func: (answer, answers, questions) => {
      return answer.q_id + "0";
    },
    q_child_nodes: [0],
    q_validation_func: (value, answer, additionalData) => {
      if (value < 0) {
        return false; //isValid
      }
      return true; //isValid
    },
    q_invalid_answer_help_text: (value, answer, additionalData) =>
      "Please enter a value above 0",
    a_error: false,
  },
  {
    q_id: "0000",
    q_title: "Annual insurance",
    q_description: (answers, additionalData) => {
      return "What is actual or estimated annual insurance?";
    },
    node_type: "node",
    a_type: "currency",
    a_input_type: "number",
    a_opts: (answers) => {
      return [];
    },
    q_next_step_func: (answer, answers, questions) => {
      return answer.q_id + "0";
    },
    q_child_nodes: [0],
    q_validation_func: (value, answer, additionalData) => {
      if (value < 0) {
        return false; //isValid
      }
      return true; //isValid
    },
    q_invalid_answer_help_text: (value, answer, additionalData) =>
      "Please enter a value above 0",
    a_error: false,
  },
  {
    q_id: "00000",
    q_title: "Annual HOA",
    q_description: (answers, additionalData) => {
      return "What is actual or estimated annual HOA?";
    },
    node_type: "node",
    a_type: "currency",
    a_input_type: "number",
    a_opts: (answers) => {
      return [];
    },
    q_next_step_func: (answer, answers, questions) => {
      return answer.q_id + "0";
    },
    q_child_nodes: [0],
    q_validation_func: (value, answer, additionalData) => {
      if (value < 0) {
        return false; //isValid
      }
      return true; //isValid
    },
    q_invalid_answer_help_text: (value, answer, additionalData) =>
      "Please enter a value above 0",
    a_error: false,
  },
  {
    q_id: "000000",
    q_title: "Loan purpose",
    q_description: (answers, additionalData) => {
      return "Is this a DSCR purchase or a DSCR refinance? Purpose affects the fields.";
    },
    node_type: "node",
    a_type: "mc",
    a_input_type: "",
    a_opts: (answers) => {
      return ["Purchase", "Refinance"];
    },
    q_next_step_func: (answer, answers, questions) => {
      if (answer.a_string === "Refinance") {
        return answer.q_id + "1";
      }

      return answer.q_id + "0";
    },
    q_child_nodes: [0, 1],
    q_validation_func: (value, answer, additionalData) => {
      // MC so any choise is valid
      return true; //isValid
    },
    q_invalid_answer_help_text: (value, answer, additionalData) => "",
    a_error: false,
  },
  {
    q_id: "0000000",
    q_title: "Purchase price",
    q_description: (answers, additionalData) => {
      return "What is the purchase price of the property?";
    },
    node_type: "node",
    a_type: "currency",
    a_input_type: "number",
    a_opts: (answers) => {
      return [0];
    },
    q_next_step_func: (answer, answers, questions) => {
      // show final quote
      return answer.q_id + "0";
    },
    q_child_nodes: [0],
    q_validation_func: (value, answer, additionalData) => {
      if (value < 1) {
        return false; //isValid
      }
      return true; //isValid
    },
    q_invalid_answer_help_text: (value, answer, additionalData) =>
      "Please enter a dollar amount above 0",
    a_error: false,
  },
  {
    q_id: "0000001",
    q_title: "Estimated AS-IS value",
    q_description: (answers, additionalData) => {
      return "What AS-IS value would the property appraise for?";
    },
    node_type: "node",
    a_type: "currency",
    a_input_type: "number",
    a_opts: (answers) => {
      return [0];
    },
    q_next_step_func: (answer, answers, questions) => {
      return answer.q_id + "0";
    },
    q_child_nodes: [0, 1],
    q_validation_func: (value, answer, additionalData) => {
      if (value < 1) {
        return false; //isValid
      }
      return true; //isValid
    },
    q_invalid_answer_help_text: (value, answer, additionalData) =>
      "Please enter a dollar amount above 0",
    a_error: false,
  },
  {
    q_id: "00000010",
    q_title: "Existing debt",
    q_description: (answers, additionalData) => {
      return "Do you owe anything on this property?";
    },
    node_type: "node",
    a_type: "currency",
    a_input_type: "number",
    a_opts: (answers) => {
      return [0];
    },
    q_next_step_func: (answer, answers, questions) => {
      // folds back to main path
      return "00000000";
    },
    q_child_nodes: [0],
    q_validation_func: (value, answer, additionalData) => {
      if (value < 0) {
        return false; //no valid if below 0
      }
      return true; //isValid
    },
    q_invalid_answer_help_text: (value, answer, additionalData) =>
      "Please enter a positive dollar amount",
    a_error: false,
  },
  {
    q_id: "00000000",
    q_title: "Loan amount",
    q_description: (answers, additionalData) => {
      return "What is the size of the loan?";
    },
    node_type: "node",
    a_type: "currency",
    a_input_type: "number",
    a_opts: (answers) => {
      return [0];
    },
    q_next_step_func: (answer, answers, questions) => {
      // show final quote
      return answer.q_id + "0";
    },
    q_child_nodes: [0],
    q_validation_func: (value, answer, additionalData) => {
      if (value < 1) {
        return false; //isValid
      }
      return true; //isValid
    },
    q_invalid_answer_help_text: (value, answer, additionalData) =>
      "Please enter a dollar amount above 0",
    a_error: false,
  },
  {
    q_id: "000000000",
    q_title: "Loan to value (LTV)",
    q_description: (answers, additionalData) => {
      const loanQuoteData = additionalData?.LoanQuoteData;
      let maxLTV;
      // Find the highest numeric LTV key in loanQuoteData and ensure value does not exceed it
      if (loanQuoteData && typeof loanQuoteData === "object") {
        // Get all keys that are numbers (LTVs)
        const ltvKeys = Object.keys(loanQuoteData)
          .map((key) => parseFloat(key))
          .filter((num) => !isNaN(num));
        if (ltvKeys.length > 0) {
          maxLTV = Math.max(...ltvKeys);
        }
      }

      if (maxLTV) {
        return (
          <>
            LTV for this loan is capped at <strong>{maxLTV}%</strong> due to
            economics of the deal and our underwriting standards
          </>
        );
      } else {
        return "What loan to value (LTV)? Common values are between 50 and 80";
      }
    },
    node_type: "node",
    a_type: "percent",
    a_input_type: "number",
    a_opts: (answers) => {
      return [0];
    },
    q_next_step_func: (answer, answers, questions) => {
      return answer.q_id + "0";
    },
    q_child_nodes: [0],
    q_validation_func: (value, answer, additionalData) => {
      const ltvValidation = validateLTVAgainstTiers(value, additionalData);

      return ltvValidation.isValid;
    },
    q_invalid_answer_help_text: (value, answer, additionalData) => {
      const loanQuoteData = additionalData?.LoanQuoteData;
      let maxLTV;
      // Find the highest numeric LTV key in loanQuoteData and ensure value does not exceed it
      if (loanQuoteData && typeof loanQuoteData === "object") {
        // Get all keys that are numbers (LTVs)
        const ltvKeys = Object.keys(loanQuoteData)
          .map((key) => parseFloat(key))
          .filter((num) => !isNaN(num));
        if (ltvKeys.length > 0) {
          maxLTV = Math.max(...ltvKeys);
        }
      }

      if (maxLTV) {
        return `Please enter a value between 0 and ${maxLTV} (the maximum LTV available for this deal)`;
      }
      return "Please enter a value between 0 and 80 (a common range of offered LTVs)";
    },
    a_error: false,
  },
  {
    q_id: "0000000000",
    q_title: "Credit spread",
    q_description: (answers, additionalData) => {
      return "This is your personalized credit spread above 5 year treasury. It depends on credit score, level of experience and LTV of the loan. If you adjust LTV, this spread will change according to the chart below.";
    },
    node_type: "node",
    a_type: "percent",
    a_input_type: "number",
    a_opts: (answers) => {
      return [0];
    },
    q_next_step_func: (answer, answers, questions) => {
      return answer.q_id + "0";
    },
    q_child_nodes: [0],
    q_validation_func: (value, answer, additionalData) => {
      return true; //isValid
    },
    q_invalid_answer_help_text: (value, answer, additionalData) => "",
    a_error: false,
  },
  {
    q_id: "00000000000",
    q_title: "Prepayment penalty",
    q_description: (answers, additionalData) => {
      return "Does this loan have a prepayment penalty? If so, please describe the terms.";
    },
    node_type: "node",
    a_type: "dropdown",
    a_opts: (answers) => {
      return [];
    },
    q_next_step_func: (answer, answers, questions) => {
      return answer.q_id + "0";
    },
    q_child_nodes: [0],
    q_validation_func: (value, answer, additionalData) => {
      return true; //isValid
    },
    q_invalid_answer_help_text: (value, answer, additionalData) => "",
    a_error: false,
  },
  {
    q_id: "000000000000",
    q_title: "Prepayment spread",
    q_description: (answers, additionalData) => {
      return "Change prepayment penalty dropdown above to see associated prepayment spread.";
    },
    node_type: "node",
    a_type: "percent",
    a_input_type: "number",
    a_opts: (answers) => {
      return [0];
    },
    q_next_step_func: (answer, answers, questions) => {
      return answer.q_id + "0";
    },
    q_child_nodes: [0],
    q_validation_func: (value, answer, additionalData) => {
      return true; //isValid
    },
    q_invalid_answer_help_text: (value, answer, additionalData) => "",
    a_error: false,
  },
  {
    q_id: "0000000000000",
    q_title: "Buydown points",
    q_description: (answers, additionalData) => {
      return "Number of buydown points to reduce the interest rate. Each point typically reduces the rate by 0.25% and costs 1% of the loan amount.";
    },
    node_type: "node",
    a_type: "dropdown",
    a_opts: (answers) => {
      return [0];
    },
    q_next_step_func: (answer, answers, questions) => {
      return answer.q_id + "0";
    },
    q_child_nodes: [0],
    q_validation_func: (value, answer, additionalData) => {
      if (value < -10 || value > 10) {
        return false; //isValid
      }
      return true; //isValid
    },
    q_invalid_answer_help_text: (value, answer, additionalData) =>
      "Please enter a value between -10 and 10",
    a_error: false,
  },
  {
    q_id: "00000000000000",
    q_title: "Buydown amount",
    q_description: (answers, additionalData) => {
      return "The total dollar amount paid upfront to reduce the interest rate through buydown points";
    },
    node_type: "leaf",
    a_type: "currency",
    a_input_type: "number",
    a_opts: (answers) => {
      return [];
    },
    q_next_step_func: (answer, answers, questions) => {
      return answer.q_id + "0";
    },
    q_child_nodes: [],
    q_validation_func: (value, answer, additionalData) => {
      return true; //isValid
    },
    q_invalid_answer_help_text: (value, answer, additionalData) => "",
    a_error: false,
    a_string: "", // Will be calculated dynamically
  },
  {
    q_id: "000000000000000",
    q_title: "Buydown spread",
    q_description: (answers, additionalData) => {
      return "The spread reduction applied to the interest rate based on buydown points";
    },
    node_type: "leaf",
    a_type: "percent",
    a_input_type: "number",
    a_opts: (answers) => {
      return [];
    },
    q_next_step_func: (answer, answers, questions) => {
      return answer.q_id + "0";
    },
    q_child_nodes: [],
    q_validation_func: (value, answer, additionalData) => {
      return true; //isValid
    },
    q_invalid_answer_help_text: (value, answer, additionalData) => "",
    a_error: false,
    a_string: "", // Will be calculated dynamically
  },
  {
    q_id: "0000000000000000",
    q_title: "5 year treasury",
    q_description: (answers, additionalData) => {
      return "Current 5 year treasury rate. This rate is NOT FIXED until loan is in APPROVED status.";
    },
    node_type: "node",
    a_type: "percent",
    a_input_type: "number",
    a_opts: (answers) => {
      return [0];
    },
    q_next_step_func: (answer, answers, questions) => {
      return answer.q_id + "0";
    },
    q_child_nodes: [0],
    q_validation_func: (value, answer, additionalData) => {
      return true; //isValid
    },
    q_invalid_answer_help_text: (value, answer, additionalData) => "",
    a_error: false,
  },
  {
    q_id: "00000000000000000",
    q_title: "Interest rate",
    q_description: (answers, additionalData) => {
      return "The rate applied to the loan amount to compute monthly payments";
    },
    node_type: "node",
    a_type: "percent",
    a_input_type: "number",
    a_opts: (answers) => {
      return [0];
    },
    q_next_step_func: (answer, answers, questions) => {
      return answer.q_id + "0";
    },
    q_child_nodes: [0],
    q_validation_func: (value, answer, additionalData) => {
      return true; //isValid
    },
    q_invalid_answer_help_text: (value, answer, additionalData) => "",
    a_error: false,
  },
  {
    q_id: "000000000000000000",
    q_title: "Origination fee",
    q_description: (answers, additionalData) => {
      return `This is the only fee that goes directly to OfferMarket and covers our operating costs to process and underwrite the Loan File.`;
    },
    node_type: "node",
    a_type: "currency",
    a_input_type: "number",
    a_opts: (answers) => {
      return [];
    },
    q_next_step_func: (answer, answers, questions) => {
      return answer.q_id + "0";
    },
    q_child_nodes: [0],
    q_validation_func: (value, answer, additionalData) => {
      if (value < 0) {
        return false; //isValid
      }
      return true; //isValid
    },
    q_invalid_answer_help_text: (value, answer, additionalData) =>
      "Please enter a value above 0",
    a_error: false,
  },
  {
    q_id: "0000000000000000000",
    q_title: "Loan fees",
    q_description: (answers, additionalData) => {
      const loanFeesAnswer = answers?.find(
        (answer) => answer?.q_title === "Loan fees"
      );
      const loanFees = loanFeesAnswer
        ? parseFloat(loanFeesAnswer.a_string)
        : 1995;

      // This simple check is necessary because no other simple way to communicate which capital provider is selected
      if (loanFees > 2000) {
        return (
          <Box>
            <Box>
              This a fixed fee of ${formatMoney(parseInt(loanFees), 0)} that
              includes several third party fees (it doesn't go to OfferMarket).
              It is necessary to verify documents for underwriting and reduce
              single party risk of the inputs that make up the Loan File.
              Specifically, it includes:
            </Box>
            <Box mt={2}>
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                <li>Underwriting fee: $995</li>
                <li>Legal/doc prep fee: $995</li>
                <li>Tax services fee: $225</li>
                <li>Servicing setup fee: $30</li>
              </ul>
            </Box>
          </Box>
        );
      }

      return `This a fixed fee of $${formatMoney(
        parseInt(loanFees),
        0
      )} that includes several third party fees (it doesn't go to OfferMarket). It is necessary to verify documents for underwriting and reduce single party risk of the inputs that make up the Loan File. Specifically, it includes underwriting fee which is used to cover the costs of researching and verifying the information in the loan file. Appraisal risk review, which employes a third party to certify the accuracy of the appraisal report. CoreLogic report, a third party report which is used to access the risks and trends that apply to the underlying collateral (the property) used to secure this loan.`;
    },
    node_type: "node",
    a_type: "currency",
    a_input_type: "number",
    a_opts: (answers) => {
      return [];
    },
    q_next_step_func: (answer, answers, questions) => {
      return answer.q_id + "0";
    },
    q_child_nodes: [0],
    q_validation_func: (value, answer, additionalData) => {
      if (value < 0) {
        return false; //isValid
      }
      return true; //isValid
    },
    q_invalid_answer_help_text: (value, answer, additionalData) =>
      "Please enter a value above 0",
    a_error: false,
  },
  {
    q_id: "00000000000000000000",
    q_title: "Title fees",
    q_description: (answers, additionalData) => {
      const loanPurpose = answers?.filter((answer) => {
        return answer?.q_title === "Loan purpose";
      })?.[0]?.a_string;

      const LTV = answers?.filter((answer) => {
        return answer?.q_title === "Loan to value (LTV)";
      })?.[0]?.a_string;

      let propertyValue;
      if (loanPurpose === "Purchase") {
        const purchasePrice = answers.filter((answer) => {
          return answer.q_title === "Purchase price";
        })[0]?.a_string;
        propertyValue = purchasePrice;
      }

      if (loanPurpose === "Refinance") {
        const estimatedAsIsValue = answers.filter((answer) => {
          return answer.q_title === "Estimated AS-IS value";
        })[0]?.a_string;
        propertyValue = estimatedAsIsValue;
      }

      const loanAmount = propertyValue * (LTV / 100);

      return `Estimate of Title fees is usually around 1% of loan amount which comes out to ${formatMoney(
        parseInt(loanAmount) * 0.01,
        0
      )}`;
    },
    node_type: "node",
    a_type: "currency",
    a_input_type: "number",
    a_opts: (answers) => {
      return [];
    },
    q_next_step_func: (answer, answers, questions) => {
      return answer.q_id + "0";
    },
    q_child_nodes: [0],
    q_validation_func: (value, answer, additionalData) => {
      if (value < 0) {
        return false; //isValid
      }
      return true; //isValid
    },
    q_invalid_answer_help_text: (value, answer, additionalData) =>
      "Please enter a value above 0",
    a_error: false,
  },
  {
    q_id: "000000000000000000000",
    q_title: "Escrow",
    q_description: (answers, additionalData) => {
      const escrowAnswer = answers?.find(
        (answer) => answer?.q_title === "Escrow"
      )?.a_string;

      return (
        <>
          {`The Escrow amount is $${formatMoney(
            escrowAnswer || 0,
            0
          )}. This is the beginning balance that will be funded into your escrow account via the settlement statement. The servicer will be responsible for paying your taxes, insurance and HOA dues (if applicable) from your escrow account. The servicer will periodically analyze your escrow account to insure it is not over or under-funded. For more information, use our `}
          <a
            href="https://www.offermarket.us/blog/escrow-calculator"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "#1976d2", textDecoration: "underline" }}
          >
            Escrow Calculator
          </a>
          .
        </>
      );
    },
    node_type: "node",
    a_type: "currency",
    a_input_type: "number",
    a_opts: (answers) => {
      return [];
    },
    q_next_step_func: (answer, answers, questions) => {
      return answer.q_id + "0";
    },
    q_child_nodes: [0],
    q_validation_func: (value, answer, additionalData) => {
      if (value < 0) {
        return false; //isValid
      }
      return true; //isValid
    },
    q_invalid_answer_help_text: (value, answer, additionalData) =>
      "Please enter a value above 0",
    a_error: false,
  },
  {
    q_id: "0000000000000000000000",
    q_title: "Prepaid interest",
    q_description: (answers, additionalData) => {
      // Just filter for "Prepaid interest" from answers, do not calculate here
      const prepaidInterestAnswer = answers?.find(
        (answer) => answer?.q_title === "Prepaid interest"
      )?.a_string;

      return `Per industry guidelines, you will be required to pay prorated interest for the number of days you hold the loan during the month in which your loan is funded. This is calculated by determining the number of days from your actual or estimated closing date through the final day of that month and multiplying it by the daily rate of interest. If loan is closed today this amount would be $${formatMoney(
        parseInt(prepaidInterestAnswer) || 0,
        0
      )}`;
    },
    node_type: "node",
    a_type: "currency",
    a_input_type: "number",
    a_opts: (answers) => {
      return [];
    },
    q_next_step_func: (answer, answers, questions) => {
      return answer.q_id + "0";
    },
    q_child_nodes: [0],
    q_validation_func: (value, answer, additionalData) => {
      if (value < 0) {
        return false; //isValid
      }
      return true; //isValid
    },
    q_invalid_answer_help_text: (value, answer, additionalData) =>
      "Please enter a value above 0",
    a_error: false,
  },
  {
    q_id: "00000000000000000000000",
    q_title: "Prepaid insurance",
    q_description: (answers, additionalData) => {
      const annualInsurance = answers?.filter((answer) => {
        return answer?.q_title === "Annual insurance";
      })?.[0]?.a_string;

      return `At closing, lenders often require that the 1st year of insurance is covered. A rough estimate of annual insurance is $${formatMoney(
        parseInt(annualInsurance),
        0
      )}`;
    },
    node_type: "node",
    a_type: "currency",
    a_input_type: "number",
    a_opts: (answers) => {
      return [];
    },
    q_next_step_func: (answer, answers, questions) => {
      return answer.q_id + "0";
    },
    q_child_nodes: [0],
    q_validation_func: (value, answer, additionalData) => {
      if (value < 0) {
        return false; //isValid
      }
      return true; //isValid
    },
    q_invalid_answer_help_text: (value, answer, additionalData) =>
      "Please enter a value above 0",
    a_error: false,
  },
  {
    q_id: "000000000000000000000000",
    q_title: "Payment reserve",
    q_description: (answers, additionalData) => {
      return `This is the amount to be collected at closing and applied towards your first monthly payment(s). This value will either be $0 (no payment reserve) or 3 months worth of payments, depending on which institutional credit investor will be purchasing this loan. A 3 month payment reserve means you will not start making monthly payments from your bank account until month 4 payment is due and therefore this should be viewed as an asset held in escrow. Up to 3 months of payment reserve may be required because of the time it can take to transfer your loan to its long-term servicer.`;
    },
    node_type: "node",
    a_type: "currency",
    a_input_type: "number",
    a_opts: (answers) => {
      return [];
    },
    q_next_step_func: (answer, answers, questions) => {
      return answer.q_id + "0";
    },
    q_child_nodes: [0],
    q_validation_func: (value, answer, additionalData) => {
      if (value < 0) {
        return false; //isValid
      }
      return true; //isValid
    },
    q_invalid_answer_help_text: (value, answer, additionalData) =>
      "Please enter a value above 0",
    a_error: false,
  },
  {
    q_id: "0000000000000000000000000",
    q_title: "Credit score",
    q_description: (answers, additionalData) => {
      return "What is your estimated tri-merge credit score?";
    },
    node_type: "node",
    a_type: "number",
    a_input_type: "number",
    a_opts: (answers) => {
      return [];
    },
    q_next_step_func: (answer, answers, questions) => {
      // return answer.q_id + "0";
      return answer.q_id + "0";
    },
    q_child_nodes: [0],
    q_validation_func: (value, answer, additionalData) => {
      if (value < 0 || value > 800) {
        return false; //isValid
      }
      return true; //isValid
    },
    q_invalid_answer_help_text: (value, answer, additionalData) =>
      "Credit score must be between 800 and 0",
    a_error: false,
  },
  // for simplicity, hardcoding refinance type for dscr calc to always be cashout
  // this wont' be asked, but necessary for correct output
  {
    q_id: "00000000000000000000000000",
    q_title: "Refinance type",
    q_description: (answers, additionalData) => {
      return "What type of refinance do you want to complete?";
    },
    node_type: "leaf",
    a_type: "mc",
    a_input_type: "",
    a_opts: (answers) => {
      return ["Cash out refinance", "Rate term refinance"];
    },
    q_next_step_func: (answer, answers, questions) => {
      if (answer.a_string === "Yes") {
        return answer.q_id + "1";
      }
      return answer.q_id + "0";
    },
    q_child_nodes: [0, 1],
    q_validation_func: (value, answer, additionalData) => {
      // mc all are valid
      return true; //isValid
    },
    q_invalid_answer_help_text: (value, answer, additionalData) => "",
    a_error: false,
    a_string: "Cash out refinance",
  },
  {
    q_id: "000000000000000000000000000",
    q_title: "Calculation name",
    q_description: (answers, additionalData) => {
      return "What would you like to name this calculation. Ex. 'First option for 777 Happy Choice Rd.' ";
    },
    node_type: "node",
    a_type: "text",
    a_input_type: "text",
    a_opts: (answers) => {
      return [];
    },
    q_next_step_func: (answer, answers, questions) => {
      return answer.q_id + "0";
    },
    q_child_nodes: [0],
    q_validation_func: (value, answer, additionalData) => {
      if (value < 0) {
        return false; //isValid
      }
      return true; //isValid
    },
    q_invalid_answer_help_text: (value, answer, additionalData) => "",
    a_error: false,
  },
  {
    q_id: "0000000000000000000000000000",
    q_title: "Address (optional)",
    q_description: (answers, additionalData) => {
      return "Enter property address to help organize your calculations. You can also enter a state, zip or nothing, since this field is optional.";
    },
    node_type: "node",
    a_type: "address",
    a_opts: (answers) => {
      return [];
    },
    q_next_step_func: (answer, answers, questions) => {
      return answer.q_id + "0";
    },
    q_child_nodes: [0],
    q_validation_func: (value, answer, additionalData) => {
      //   make sure not false and a string of none 0 length
      if (!value && !value.length > 0) {
        return false;
      }
      return true; //isValid
    },
    q_invalid_answer_help_text: (value, answer, additionalData) =>
      "Please enter or select an address",
    a_error: false,
  },
  {
    q_id: "00000000000000000000000000000",
    q_title: "DSCR",
    q_description: (answers, additionalData) => {
      return "Debt Service Coverage Ratio - measures the property's ability to service its debt";
    },
    node_type: "leaf",
    a_type: "number",
    a_input_type: "number",
    a_opts: (answers) => {
      return [];
    },
    q_next_step_func: (answer, answers, questions) => {
      return answer.q_id + "0";
    },
    q_child_nodes: [],
    q_validation_func: (value, answer, additionalData) => {
      return true; //isValid
    },
    q_invalid_answer_help_text: (value, answer, additionalData) => "",
    a_error: false,
    a_string: "", // Will be calculated dynamically
  },
  {
    q_id: "000000000000000000000000000000",
    q_title: "Monthly cashflow",
    q_description: (answers, additionalData) => {
      return "The net monthly cash flow from the property after all expenses";
    },
    node_type: "leaf",
    a_type: "currency",
    a_input_type: "number",
    a_opts: (answers) => {
      return [];
    },
    q_next_step_func: (answer, answers, questions) => {
      return answer.q_id + "0";
    },
    q_child_nodes: [],
    q_validation_func: (value, answer, additionalData) => {
      return true; //isValid
    },
    q_invalid_answer_help_text: (value, answer, additionalData) => "",
    a_error: false,
    a_string: "", // Will be calculated dynamically
  },
  {
    q_id: "0000000000000000000000000000000",
    q_title: "Cash to close",
    q_description: (answers, additionalData) => {
      return "The total amount of cash needed to close the transaction, including all fees, closing costs, and buydown amount (if applicable)";
    },
    node_type: "leaf",
    a_type: "currency",
    a_input_type: "number",
    a_opts: (answers) => {
      return [];
    },
    q_next_step_func: (answer, answers, questions) => {
      return answer.q_id + "0";
    },
    q_child_nodes: [],
    q_validation_func: (value, answer, additionalData) => {
      return true; //isValid
    },
    q_invalid_answer_help_text: (value, answer, additionalData) => "",
    a_error: false,
    a_string: "", // Will be calculated dynamically
  },
  {
    q_id: "00000000000000000000000000000000",
    q_title: "Liquidity to verify",
    q_description: (answers, additionalData) => {
      const loanPurpose = answers.find(
        (a) => a.q_title === "Loan purpose"
      )?.a_string;

      if (loanPurpose === "Refinance") {
        return "This is a conservative estimate of the amount of liquidity we need to verify is in your possession per DSCR loan program guidelines. This figure is what the lender needs to verify but won't be paid out. If you have net proceeds, industry guidelines allow 50% of that number to be used as an offset to fees and PITIA reserve for liquidity verification purposes.";
      }

      return "This is a conservative estimate of the amount of liquidity we need to verify is in your possession per DSCR loan program guidelines. This includes Cash to close (including buydown amount), plus additional PITIA reserves.";
    },
    node_type: "leaf",
    a_type: "currency",
    a_input_type: "number",
    a_opts: (answers) => {
      return [];
    },
    q_next_step_func: (answer, answers, questions) => {
      return answer.q_id + "0";
    },
    q_child_nodes: [],
    q_validation_func: (value, answer, additionalData) => {
      return true; //isValid
    },
    q_invalid_answer_help_text: (value, answer, additionalData) => "",
    a_error: false,
    a_string: "", // Will be calculated dynamically
  },
  {
    q_id: "000000000000000000000000000000000",
    q_title: "Monthly payment",
    q_description: (answers, additionalData) => {
      return "The monthly payment that will cover loan's principal, interest, taxes, insurance, and HOA";
    },
    node_type: "leaf",
    a_type: "currency",
    a_input_type: "number",
    a_opts: (answers) => {
      return [];
    },
    q_next_step_func: (answer, answers, questions) => {
      return answer.q_id + "0";
    },
    q_child_nodes: [],
    q_validation_func: (value, answer, additionalData) => {
      return true; //isValid
    },
    q_invalid_answer_help_text: (value, answer, additionalData) => "",
    a_error: false,
    a_string: "", // Will be calculated dynamically
  },
  {
    q_id: "0000000000000000000000000000000000",
    q_title: "Total loan",
    q_description: (answers, additionalData) => {
      return "The total loan amount including all fees and costs";
    },
    node_type: "leaf",
    a_type: "currency",
    a_input_type: "number",
    a_opts: (answers) => {
      return [];
    },
    q_next_step_func: (answer, answers, questions) => {
      return answer.q_id + "0";
    },
    q_child_nodes: [],
    q_validation_func: (value, answer, additionalData) => {
      return true; //isValid
    },
    q_invalid_answer_help_text: (value, answer, additionalData) => "",
    a_error: false,
    a_string: "", // Will be calculated dynamically
  },
];

export default DSCRCalculatorQuestions;
